/**
 * DataFlowBadge Component
 *
 * Displays item count at edge midpoint during workflow execution.
 * Shows the number of items flowing through an edge with optional animation.
 */

import { memo, useMemo } from 'react';
import { EdgeLabelRenderer } from '@xyflow/react';
import { makeStyles, tokens, mergeClasses, Tooltip } from '@fluentui/react-components';

export interface DataFlowBadgeProps {
  /** X position at edge midpoint */
  labelX: number;
  /** Y position at edge midpoint */
  labelY: number;
  /** Number of items flowing through this edge */
  itemCount: number;
  /** Whether data is currently flowing (triggers animation) */
  isFlowing?: boolean;
  /** Label for the data type (e.g., "items", "records") */
  itemLabel?: string;
  /** Whether the badge should be visible */
  visible?: boolean;
}

const useStyles = makeStyles({
  badgeWrapper: {
    position: 'absolute',
    pointerEvents: 'all',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transitionProperty: 'opacity, transform',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
  },
  badgeHidden: {
    opacity: 0,
    transform: 'translate(-50%, -50%) scale(0.8)',
    pointerEvents: 'none',
  },
  badgeVisible: {
    opacity: 1,
    transform: 'translate(-50%, -50%) scale(1)',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
    cursor: 'default',
  },
  badgeFlowing: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    // Subtle pulse animation using opacity
    animationName: {
      from: { opacity: 1 },
      to: { opacity: 0.7 },
    },
    animationDuration: '0.8s',
    animationIterationCount: 'infinite',
    animationDirection: 'alternate',
    animationTimingFunction: 'ease-in-out',
    // Respect reduced motion preference
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      opacity: 1,
    },
  },
  itemCount: {
    fontVariantNumeric: 'tabular-nums',
  },
  itemLabel: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
});

/**
 * Badge component that displays data flow information on workflow edges.
 * Used in monitoring mode to show how many items passed through each connection.
 */
export const DataFlowBadge = memo<DataFlowBadgeProps>(({
  labelX,
  labelY,
  itemCount,
  isFlowing = false,
  itemLabel = 'items',
  visible = true,
}) => {
  const styles = useStyles();

  // Format large numbers with K/M suffix
  const formattedCount = useMemo(() => {
    if (itemCount >= 1_000_000) {
      return `${(itemCount / 1_000_000).toFixed(1)}M`;
    }
    if (itemCount >= 1_000) {
      return `${(itemCount / 1_000).toFixed(1)}K`;
    }
    return itemCount.toString();
  }, [itemCount]);

  // Build tooltip content
  const tooltipContent = useMemo(() => {
    const fullCount = itemCount.toLocaleString();
    return `${fullCount} ${itemLabel}${isFlowing ? ' (in progress)' : ''}`;
  }, [itemCount, itemLabel, isFlowing]);

  return (
    <EdgeLabelRenderer>
      <div
        className={mergeClasses(
          styles.badgeWrapper,
          visible ? styles.badgeVisible : styles.badgeHidden
        )}
        style={{
          transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
        }}
      >
        <Tooltip content={tooltipContent} relationship="description" positioning="above">
          <div
            className={mergeClasses(
              styles.badge,
              isFlowing && styles.badgeFlowing
            )}
            role="status"
            aria-label={tooltipContent}
          >
            <span className={styles.itemCount}>{formattedCount}</span>
            <span className={styles.itemLabel}>{itemLabel}</span>
          </div>
        </Tooltip>
      </div>
    </EdgeLabelRenderer>
  );
});

DataFlowBadge.displayName = 'DataFlowBadge';

export default DataFlowBadge;
